import { useMutation } from '@tanstack/react-query'
import { setCookie, getCookie, deleteCookie } from '@/shared/lib/formatter'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { request } from '@/shared/api/request'
import {
  parseWithSchema,
  userMeResponseSchema,
  type UserMe,
  type UserMeResponse,
} from '@/shared/api'
import { useSidebarController } from '../../../root/sidebarView'
import { useEffect, useState, useRef } from 'react'
import { useAuthStore, type PermissionMap } from '@/entities/auth/model'
import { useTheme } from '@/entities/theme/model'
import { darkTheme } from '../../../theme'

dayjs.extend(utc)

const COOKIE_KEYS = {
  TOKEN: 'token',
  USERNAME: 'username',
  NICKNAME: 'nickname',
  USER_ID: 'userId',
  SIZE: 'size',
  CURRENT_PATH: 'currentPath',
  EXPIRES_AT: 'expiresAt',
} as const

export function useUserMe() {
  const { setUser, setUserRoles, setPermissions, setViewItems, logout } = useAuthStore.getState()
  const navigate = useSafeNavigate()
  const [meta, setMeta] = useState<UserMe | null>(null)
  const viewItems = useSidebarController()
  const { theme, setTheme } = useTheme()
  const hasDispatchedPermissions = useRef(false)
  const hasNavigatedRef = useRef(false)

  const themeInitializedRef = useRef(false)

  useEffect(() => {
    if (themeInitializedRef.current) return
    themeInitializedRef.current = true

    const themeCookie = getCookie('theme')
    let parsedTheme: Record<string, any> | null = null

    try {
      parsedTheme = themeCookie ? JSON.parse(themeCookie) : null
    } catch (e) {
      console.error('Error parsing theme cookie:', e)
    }

    if (parsedTheme && typeof parsedTheme === 'object') {
      setTheme(parsedTheme as Record<string, string>)
    } else {
      const defaultTheme = theme || darkTheme
      setTheme(defaultTheme as Record<string, string>)
      setCookie('theme', JSON.stringify(defaultTheme))
      setCookie('themeName', (defaultTheme as Record<string, any>)?.themeName as string)
    }

    return () => {
      // Cleanup for Strict Mode
    }
  }, [setTheme, theme])

  const handleNavigation = useRef(() => {
    if (hasNavigatedRef.current) return

    const currentPath = getCookie(COOKIE_KEYS.CURRENT_PATH)
    const targetUrl = getCookie('targetUrl')
    const currentPathname = window.location.pathname + window.location.search

    if (currentPath && !currentPath.includes('/login') && currentPath !== currentPathname) {
      hasNavigatedRef.current = true
      navigate(currentPath, { replace: true })
      if (currentPath.startsWith('?')) {
        deleteCookie(COOKIE_KEYS.CURRENT_PATH)
      }
    } else if (targetUrl && targetUrl !== currentPathname && targetUrl !== '/') {
      hasNavigatedRef.current = true
      navigate(targetUrl, { replace: true })
    }
  }).current

  const getUserMe = useMutation<UserMeResponse, unknown, void>({
    mutationFn: async () => {
      const currentToken = getCookie(COOKIE_KEYS.TOKEN)
      const tokenExpiresAt = getCookie(COOKIE_KEYS.EXPIRES_AT)

      if (!currentToken || !tokenExpiresAt || dayjs(tokenExpiresAt).isBefore(dayjs())) {
        navigate('/login')
        deleteCookie(COOKIE_KEYS.CURRENT_PATH)
        throw new Error('Authentication failed: token missing or expired')
      }

      const raw = await request({
        url: '/me',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      return parseWithSchema(userMeResponseSchema, raw, { url: '/me' })
    },
    onSuccess: (res) => {
      try {
        if (res?.success && res.meta) {
          const { meta } = res

          const user = {
            id: meta?.id,
            fullname: meta?.fullname,
          }
          setCookie('user', JSON.stringify(user))
          setCookie('username', meta?.fullname ?? '')
          setCookie('size', '20')

          setMeta(meta)

          if (!hasDispatchedPermissions.current) {
            setUser({
              id: meta?.id,
              email: meta?.email ?? undefined,
              fullname: meta?.fullname ?? undefined,
              nickname: meta?.nickname ?? undefined,
              userId: meta?.id != null ? Number(meta.id) : undefined,
              imageUrl: meta?.photo?.path ?? undefined,
            })

            setUserRoles(meta?.roles || [])
            setPermissions((meta?.permissions2 || {}) as PermissionMap)
            setViewItems(viewItems as any as import('@/entities/auth/model').ViewItems)

            hasDispatchedPermissions.current = true
          }

          if (!hasNavigatedRef.current) {
            handleNavigation()
          }
        } else {
          throw new Error('Authentication failed: Invalid response')
        }
      } catch (error) {
        throw error
      }
    },
    onError: () => {
      deleteCookie(COOKIE_KEYS.CURRENT_PATH)
      navigate('/login')
      logout()
      hasDispatchedPermissions.current = false
    },
  })

  return getUserMe
}

export default useUserMe
